import Layout from 'components/layout/Layout'
import ProducerDetail from 'features/producer-detail/ProducerDetail'
import type { ProducerDetailProps } from 'features/producer-detail/types'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import { Language } from 'types/language'
import { DEFAULT_NAMESPACES, PRODUCERS_NAMESPACES } from 'constants/i18n'
import useWindowSize from 'hooks/useWindowSize'
import type { SeoProps } from 'components/seo/types'
import { useTranslation } from 'next-i18next/pages'
import { cmsLib, producersLib } from 'lib'
import { mapCmsBlogArticleToArticle } from 'utils/cms'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { getLowerCaseLanguage } from 'utils/language'

const ProducerDetailPage = (props: ProducerDetailProps) => {
  // Window size
  const { isTablet } = useWindowSize()

  // i18n
  const { t } = useTranslation('producer')
  const language = useGetLanguage()

  // Constants
  const { name, images, locations } = props.producer
  const producerDetails = locations?.[0]
  const uniqueLocations = new Set(locations.map((loc) => loc.location[language] || loc.location[Language.DUTCH]))
  const i18nSeoDynamicProps = {
    count: uniqueLocations.size,
    totalCountMinusOne: locations.length - 1,
    energyType: t(($) => $.seo.energyTypes[producerDetails.energyType]),
    name: name?.[language],
    location1: Array.from(uniqueLocations)[0],
    location2: uniqueLocations.size >= 2 ? Array.from(uniqueLocations)[1] : ''
  }
  const seo: SeoProps = {
    title: t(($) => $.seo.title, i18nSeoDynamicProps),
    description: t(($) => $.seo.description, { ...i18nSeoDynamicProps, energyType: i18nSeoDynamicProps.energyType?.toLowerCase() }),
    image: images?.profile?.url?.replace('w_800', 'w_1200,h_600,c_thumb')
  }

  return (
    <Layout highContrast {...{ seo }}>
      <ProducerDetail {...{ isTablet }} {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ params, locale }) => {
  const language = getLowerCaseLanguage(locale)
  const { slug } = params

  const [producer, relatedArticles] = await Promise.all([
    producersLib.getParentProducerBySlug(slug, { full: true, withExtraImages: true }),
    cmsLib.getBlogArticles(language, slug)
  ])

  if (!producer) {
    return {
      notFound: true,
      revalidate: 100
    }
  }

  const [headerObjectPosition, translations] = await Promise.all([
    producersLib.getImageObjectPosition(producer.images.header?.publicId),
    serverSideTranslations(locale, [...DEFAULT_NAMESPACES, ...PRODUCERS_NAMESPACES], i18nConfig)
  ])

  return {
    props: {
      producer,
      relatedArticle: relatedArticles.length ? mapCmsBlogArticleToArticle(relatedArticles[0]) : null,
      headerObjectPosition,
      ...translations
    },
    revalidate: 600 // re-generate page when a request comes in, once every 10 minutes
  }
}

export const getStaticPaths = async () => {
  const slugs = await producersLib.getParentProducersSlugs({ language: Language.DUTCH, params: { enabled: true } })
  const paths: { locale: string; params: { slug: string } }[] = []

  if (!slugs?.length) throw new Error('Could not fetch the parent producer slugs')

  // Add both 'nl' and 'fr' path of each existing producer to paths
  slugs.forEach((slug) => {
    const producerPaths = [
      { locale: 'nl', params: { slug } },
      { locale: 'fr', params: { slug } }
    ]
    paths.push(...producerPaths)
  })

  return {
    fallback: 'blocking', // will server-render pages on-demand if the path does not exist
    paths
  }
}

export default ProducerDetailPage
